// frontend/src/widgets/WorkflowChecklistBuilderWidget.jsx
// Project: Vision ANPR by Castle Traffic Ltd
// Author: Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Updated: 2026-10-15 BST
// Change Log:
//   2026-10-13 15:05 BST - Added checklist card authoring with drag/drop of existing workflow steps.
//   2026-10-15 BST - Display canonical workflow sequence numbers, add independently
//                    collapsible checklists, and separate scrolling workspaces.
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function WorkflowChecklistBuilderWidget({
  selectedVersion,
  steps = [],
  checklists = [],
  loading = {},
  onCreate,
  onRename,
  onDelete,
  onSetSteps,
}) {
  const canEdit = String(selectedVersion?.status || "") === "draft";
  const [name, setName] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const knownChecklistIds = useRef(new Set());
  const activeVersionId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  const versionId = selectedVersion?.id == null ? null : String(selectedVersion.id);

  useEffect(() => {
    const currentIds = new Set(checklists.map((c) => String(c.id)));
    if (activeVersionId.current !== versionId) {
      activeVersionId.current = versionId;
      knownChecklistIds.current = currentIds;
      setExpandedIds(new Set());
      setDragOverId(null);
      return;
    }
    const newlyAdded = [...currentIds].filter((id) => !knownChecklistIds.current.has(id));
    knownChecklistIds.current = currentIds;
    setExpandedIds((previous) => {
      const next = new Set([...previous].filter((id) => currentIds.has(id)));
      newlyAdded.forEach((id) => next.add(id));
      return next;
    });
  }, [versionId, checklists]);

  const orderedSteps = useMemo(
    () => [...steps].sort((a, b) =>
      Number(a.sequence_no ?? 0) - Number(b.sequence_no ?? 0) || Number(a.id) - Number(b.id)
    ),
    [steps]
  );
  const byId = useMemo(() => new Map(steps.map((step) => [String(step.id), step])), [steps]);
  const membership = useMemo(() => {
    const result = new Map();
    checklists.forEach((checklist) =>
      (checklist.steps || []).forEach((entry) =>
        result.set(String(entry.workflow_template_step_id), checklist.id)
      )
    );
    return result;
  }, [checklists]);
  const available = orderedSteps.filter(
    (step) => !step.is_system_locked && !membership.has(String(step.id))
  );

  const toggle = (id) => {
    const key = String(id);
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed || !canEdit || loading.saving) return;
    await onCreate?.({
      name: trimmed,
      assignment_mode: "unassigned",
      completion_policy: "all_required_steps",
    });
    setName("");
  };

  const drop = async (event, checklist) => {
    event.preventDefault();
    setDragOverId(null);
    if (!canEdit || loading.saving) return;
    const stepId = Number(event.dataTransfer.getData("text/workflow-step-id"));
    if (!Number.isSafeInteger(stepId) || !byId.has(String(stepId))) return;
    if (membership.has(String(stepId))) return;
    const ids = (checklist.steps || []).map((entry) => Number(entry.workflow_template_step_id));
    if (!ids.includes(stepId)) await onSetSteps?.(checklist.id, [...ids, stepId]);
    setExpandedIds((previous) => new Set([...previous, String(checklist.id)]));
  };

  const remove = async (checklist, stepId) => {
    if (!canEdit || loading.saving) return;
    const ids = (checklist.steps || [])
      .map((entry) => Number(entry.workflow_template_step_id))
      .filter((id) => id !== Number(stepId));
    await onSetSteps?.(checklist.id, ids);
  };

  const move = async (checklist, index, direction) => {
    if (!canEdit || loading.saving) return;
    const ids = (checklist.steps || []).map((entry) => Number(entry.workflow_template_step_id));
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= ids.length) return;
    [ids[index], ids[nextIndex]] = [ids[nextIndex], ids[index]];
    await onSetSteps?.(checklist.id, ids);
  };

  const stepLabel = (step, fallbackId) => (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className="inline-flex w-9 shrink-0 justify-center rounded bg-slate-200 px-1 py-1 text-xs font-bold tabular-nums text-slate-800"
        title="Workflow step sequence number"
      >
        {step?.sequence_no ?? "—"}
      </span>
      <span className="min-w-0 break-words">{step?.title || `Step ${fallbackId}`}</span>
    </span>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-1" style={{ color: "#1f2937" }}>
      <div className="shrink-0">
        <div className="font-bold">Checklists</div>
        <div className="text-xs text-slate-500">
          Group existing workflow steps into one assignable engineer checklist. Steps remain atomic workflow steps.
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void add();
            }
          }}
          placeholder="Checklist name e.g. Deploy ATC"
          disabled={!canEdit || loading.saving}
          className="min-w-0 flex-1 rounded border border-slate-300 px-3 py-2"
        />
        <button
          type="button"
          disabled={!canEdit || loading.saving || !name.trim()}
          onClick={add}
          className="rounded bg-orange-500 px-3 py-2 font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Add Checklist
        </button>
      </div>

      {!selectedVersion && (
        <div className="rounded border p-3 text-sm">Select a workflow version.</div>
      )}

      {selectedVersion && (
        <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(220px,0.7fr)_minmax(320px,1.3fr)]">
          <section className="flex min-h-0 flex-col rounded border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 shrink-0 font-bold">Available Steps ({available.length})</div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1">
              {available.map((step) => (
                <div
                  key={step.id}
                  draggable={canEdit && !loading.saving}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/workflow-step-id", String(step.id));
                  }}
                  className={`rounded border bg-white p-2 text-sm ${canEdit ? "cursor-grab" : "cursor-default"}`}
                >
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true" className="text-slate-500">≡</span>
                    {stepLabel(step, step.id)}
                  </div>
                </div>
              ))}
              {!available.length && (
                <div className="text-xs text-slate-500">
                  All editable steps are currently in checklists.
                </div>
              )}
            </div>
          </section>

          <section className="flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
            {checklists.map((checklist) => {
              const expanded = expandedIds.has(String(checklist.id));
              const entries = checklist.steps || [];
              return (
                <div
                  key={checklist.id}
                  onDragOver={(event) => {
                    if (!canEdit || loading.saving) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    setDragOverId(String(checklist.id));
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) setDragOverId(null);
                  }}
                  onDrop={(event) => drop(event, checklist)}
                  className={`shrink-0 rounded-xl border bg-white p-3 shadow-sm ${
                    dragOverId === String(checklist.id)
                      ? "border-orange-500 ring-2 ring-orange-200"
                      : "border-slate-200"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggle(checklist.id)}
                      aria-expanded={expanded}
                      aria-label={`${expanded ? "Collapse" : "Expand"} ${checklist.name}`}
                      className="rounded border border-slate-300 px-2 py-1 text-sm font-bold"
                    >
                      {expanded ? "▾" : "▸"}
                    </button>
                    <span className="min-w-0 flex-1 truncate font-bold" title={checklist.name}>
                      {checklist.name}
                    </span>
                    <span className="shrink-0 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">
                      {entries.length} {entries.length === 1 ? "step" : "steps"}
                    </span>
                    <button
                      type="button"
                      disabled={!canEdit || loading.saving}
                      onClick={() =>
                        window.confirm(`Delete checklist '${checklist.name}'? Steps will not be deleted.`) &&
                        onDelete?.(checklist.id)
                      }
                      className="rounded border px-2 py-1 text-xs disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>

                  {expanded && (
                    <div className="mt-3 space-y-2">
                      <label className="block text-xs font-semibold text-slate-600">
                        Checklist name
                        <input
                          key={`${checklist.id}:${checklist.name}`}
                          defaultValue={checklist.name}
                          disabled={!canEdit || loading.saving}
                          onBlur={(event) => {
                            const value = event.target.value.trim();
                            if (value && value !== checklist.name) onRename?.(checklist.id, value);
                          }}
                          className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-semibold text-slate-800"
                        />
                      </label>
                      <div className="min-h-16 rounded border border-dashed border-slate-300 p-2">
                        <div className="mb-2 text-xs text-slate-500">Drag available steps here</div>
                        {entries.map((entry, index) => {
                          const step = byId.get(String(entry.workflow_template_step_id));
                          return (
                            <div
                              key={entry.id ?? `${entry.workflow_template_step_id}-${index}`}
                              className="mb-1 flex items-center gap-2 rounded border bg-slate-50 p-2 text-sm"
                            >
                              <span className="min-w-0 flex-1">
                                {stepLabel(step, entry.workflow_template_step_id)}
                              </span>
                              <button
                                type="button"
                                title="Move up within checklist"
                                disabled={!canEdit || loading.saving || index === 0}
                                onClick={() => move(checklist, index, -1)}
                                className="px-1 disabled:opacity-30"
                              >↑</button>
                              <button
                                type="button"
                                title="Move down within checklist"
                                disabled={!canEdit || loading.saving || index === entries.length - 1}
                                onClick={() => move(checklist, index, 1)}
                                className="px-1 disabled:opacity-30"
                              >↓</button>
                              <button
                                type="button"
                                title="Remove from checklist (does not delete workflow step)"
                                disabled={!canEdit || loading.saving}
                                onClick={() => remove(checklist, entry.workflow_template_step_id)}
                                className="px-1 disabled:opacity-30"
                              >×</button>
                            </div>
                          );
                        })}
                      </div>
                      <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked readOnly />
                        Mark checklist complete when all required steps are complete
                      </label>
                    </div>
                  )}
                  {!expanded && dragOverId === String(checklist.id) && (
                    <div className="mt-2 text-xs text-orange-700">Drop to add step</div>
                  )}
                </div>
              );
            })}
            {!checklists.length && (
              <div className="rounded border border-dashed p-5 text-center text-sm text-slate-500">
                No checklists yet. Add one, then drag existing steps into it.
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
